import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "@Components/Common/Spinner";
import theme from "@Style/theme";
import { Box, Heading } from "grommet";
import useBookingAction from "@Hooks/useBooking/useBookingAction";
import {
  setBookingId,
  setClientId,
  setCompanySettings,
  setPatient,
  setPortalSettings
} from "@Hooks/useBooking/actions";
import { fetchPortalSettings } from "@Api/filter";
import { finalizeLoginSession } from "@Api/auth";
import { LoginSessionParameters } from "@Api/authTypes";
import { useIntl } from "react-intl";
import messages from "@Components/Login/messages";
import Button from "@Components/Common/Button";
import {getLoginErrorMessage} from "@Components/Login/common";
import axios from "axios";


const FederatedLoginCallback = () => {
  const [queryParams] = useSearchParams();
  const bookingDispatch = useBookingAction();
  const { formatMessage } = useIntl();

  const [error, setError] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [portalSettingsLoaded, setPortalSettingsLoaded] = useState(false);

  const navigate = useNavigate();

  const loginParameters = {
    grandIdSession: queryParams.get("grandidsession") ?? "",
    clientId: queryParams.get("clientId") ?? "",
    bookingId: queryParams.get("bookingId") ?? "",
    reservation: queryParams.get("reservation") ?? ""
  };

  useEffect(() => {
    if (!queryParams) {
      return;
    }

    const verifySession = async (params: LoginSessionParameters) => {
      try {
        let loginResponse = await finalizeLoginSession(params);
        if (!ignore) {
          bookingDispatch(setPatient(loginResponse.patient));
          if (loginResponse.company?.settings) {
            bookingDispatch(setCompanySettings(loginResponse.company.settings));
          }
          if (loginResponse.company?.customerNumber) {
            bookingDispatch(setClientId(loginResponse.company.customerNumber));
          }
          if (params.bookingId) {
            bookingDispatch(setBookingId(params.bookingId));
          }
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setError(getLoginErrorMessage(error?.response?.data?.message, formatMessage));
        } else {
          setError(formatMessage(messages.loginFailed));
        }
      }
    };

    const loadPortalSettings = async () => {
      try {
        const portalSettings = await fetchPortalSettings();
        if (!ignore) {
          bookingDispatch(setPortalSettings(portalSettings));
        }
      } catch (error) {
        setError(formatMessage(messages.loginFailed));
      }
    };

    let ignore = false;

    verifySession(loginParameters).then(() => setLoggedIn(true));
    loadPortalSettings().then(() => setPortalSettingsLoaded(true));

    return () => {
      ignore = true;
    };
  }, []);

  const reservationParam = loginParameters.reservation
      ? `&reservation=${loginParameters.reservation}`
      : '';

  if ((!loggedIn || !portalSettingsLoaded) && !error) {
    return (
      <Box pad={"medium"} gap={"medium"}>
        <Spinner color={theme.global.colors.text} size={35} />
        <Heading level={2} alignSelf={"center"} textAlign={"center"}>
          {formatMessage(messages.loggingIn)}
        </Heading>
      </Box>
    );
  } else if (error) {
    return (
      <Box pad={"medium"} gap={"medium"}>
        <Heading level={2} alignSelf={"center"} textAlign={"center"}>
          {error}
        </Heading>
        <Button
          alignSelf={"center"}
          width={"100px"}
          label={formatMessage(messages.button)}
          secondary
          type="button"
          onClick={() => navigate(`/?client_no=${loginParameters.clientId+reservationParam}`)}
        />
      </Box>);
  } else {
    return loginParameters.bookingId ?
        <Navigate to={`/book/${loginParameters.bookingId}?client_no=${loginParameters.clientId}`} /> :
        <Navigate to={`/?client_no=${loginParameters.clientId+reservationParam}`} />;
  }
};

export default FederatedLoginCallback;